import {
  IAutomationActionTemplateLessonlyAssignToContent,
  IAutomationTemplate
} from '@dadohr/shared/domainTypes/experience';
import { FormControlLabel, InputAdornment, Switch } from '@material-ui/core';
import { compact } from 'lodash';
import React, { SetStateAction } from 'react';
import { AutocompleteSingle } from '../../../../../../../components/Autocomplete';
import { ColoredChip } from '../../../../../../../components/ColoredChip';
import { DataFieldWithLabel } from '../../../../../../../components/DataFieldWithLabel';
import { FormRow } from '../../../../../../../components/Form/FormRow';
import { InfoCircle } from '../../../../../../../components/InfoCircle';
import { useUnsafeTranslation } from '../../../../../../../i18next';
import { FlexContainer } from '../../../../../../../layout/Flex';
import { joinWithCommaAndAnd } from '../../../../../../../services/enumerate';
import {
  ONE_DAY,
  toWeekAndDayDuration
} from '../../../../../../../services/time';
import { getLessonlyContentOptions } from '../../../../../../Org/pages/Integrations/integrations/lessonly/service';
import { Duration } from '../../../TaskBuilder/components/TimeSelectors';
import { TaskBuilderContext } from '../../../TaskBuilder/types';
import { ActionActorSelectorMulti, actionActorToString } from '../../shared';
import {
  AutomationBuilderActionValidationState,
  AutomationBuilderActionValidationStateItem,
  VALIDATORS_LESSONLY_ASSIGN_TO_CONTENT,
  useFieldValidator
} from '../../validation';

const LessonlyContentTypeChip = ({
  type
}: {
  type: IAutomationActionTemplateLessonlyAssignToContent['content']['type'];
}) => {
  const { t } = useUnsafeTranslation();
  const width = 70;
  switch (type) {
    case 'lesson':
      return (
        <ColoredChip width={width} color="yellow" mode="light" size="small">
          {t('Lesson')}
        </ColoredChip>
      );
    case 'path':
      return (
        <ColoredChip width={width} color="blue" mode="light" size="small">
          {t('Path')}
        </ColoredChip>
      );
    case 'certification':
      return (
        <ColoredChip width={width} color="green" mode="light" size="small">
          {t('Cert')}
        </ColoredChip>
      );

    default:
      console.warn('UNKNOWN_LESSONLY_CONTENT_TYPE', type);
      return null;
  }
};

const ContentSelector = ({
  orgId,
  value,
  onChange,

  helperText,
  error
}: {
  orgId: string;
  value: IAutomationActionTemplateLessonlyAssignToContent['content'];
  onChange: (
    nextValue: IAutomationActionTemplateLessonlyAssignToContent['content']
  ) => void;

  helperText?: React.ReactNode;
  error?: boolean;
}) => {
  const { t } = useUnsafeTranslation();

  return (
    <AutocompleteSingle
      label={t('Assign to')}
      fullWidth
      value={value}
      onChange={(nextValue) => {
        if (nextValue) {
          onChange(nextValue);
        }
      }}
      helperText={helperText}
      error={error}
      disabledClearable={true}
      options={() =>
        // TODO - probably not interested in archived content etc
        getLessonlyContentOptions(orgId).then((cs) =>
          compact(
            cs.map<
              IAutomationActionTemplateLessonlyAssignToContent['content'] | null
            >((c) => {
              if (c.resource_type === 'lesson') {
                return {
                  id: c.id,
                  name: c.title,
                  type: 'lesson'
                };
              }
              if (c.resource_type === 'path') {
                return {
                  id: c.id,
                  name: c.title,
                  type: c.is_certification ? 'certification' : 'path'
                };
              }
              console.warn('UNHANDLED_LESSONLY_CONTENT_TYPE', c);
              return null;
            })
          )
        )
      }
      getOptionLabel={(o) => o.name}
      renderOption={(o) => (
        <FlexContainer>
          <LessonlyContentTypeChip type={o.type} />
          <div>{o.name}</div>
        </FlexContainer>
      )}
      InputProps={{
        startAdornment: value.id ? (
          <InputAdornment position="start">
            <LessonlyContentTypeChip type={value.type} />
          </InputAdornment>
        ) : null
      }}
    />
  );
};

export const ActionSettingsLessonlyAssignToContent = ({
  value,
  onChange,
  expTpl,
  aTpl,
  validation,
  setValidation
}: {
  value: IAutomationActionTemplateLessonlyAssignToContent;
  onChange: (
    nextValue: IAutomationActionTemplateLessonlyAssignToContent
  ) => void;
  aTpl: IAutomationTemplate;
  expTpl: TaskBuilderContext;
  validation: AutomationBuilderActionValidationStateItem | null;
  setValidation: React.Dispatch<
    SetStateAction<AutomationBuilderActionValidationState>
  >;
}) => {
  const { t } = useUnsafeTranslation();

  const validatePplToAdd = useFieldValidator(
    setValidation,
    VALIDATORS_LESSONLY_ASSIGN_TO_CONTENT,
    'pplToAdd'
  );
  const invalidPpltoAdd = validation?.fields.pplToAdd || null;

  // const validateContent = useFieldValidator(
  //   setValidation,
  //   VALIDATORS_LESSONLY_ASSIGN_TO_CONTENT,
  //   'content'
  // );
  const invalidContent = validation?.fields.content || null;

  return (
    <div>
      <FormRow>
        <ActionActorSelectorMulti
          label={t('Who should be assigned')}
          value={value.pplToAdd}
          onChange={(nextPplToAdd) => {
            const nextValue = { ...value, pplToAdd: nextPplToAdd };
            invalidPpltoAdd && validatePplToAdd(nextValue, expTpl, aTpl);
            return onChange(nextValue);
          }}
          expTpl={expTpl}
          fullWidth
          error={!!invalidPpltoAdd}
          helperText={invalidPpltoAdd}
          onBlur={() => validatePplToAdd(value, expTpl, aTpl)}
        />
      </FormRow>
      <FormRow>
        <ContentSelector
          orgId={expTpl.orgId}
          value={value.content}
          helperText={invalidContent}
          error={!!invalidContent}
          onChange={(nextContent) => {
            onChange({ ...value, content: nextContent });
          }}
        />
      </FormRow>

      <FormRow>
        <div style={{ width: '100%' }}>
          <FormRow>
            <FormControlLabel
              control={
                <Switch
                  checked={value.duration !== null}
                  onChange={() =>
                    onChange({
                      ...value,
                      duration: value.duration === null ? ONE_DAY * 7 : null
                    })
                  }
                  color="primary"
                />
              }
              label={t('Time to complete the assignment')}
            />
          </FormRow>
          {value.duration !== null && (
            <FormRow>
              <Duration
                value={value.duration}
                onChange={(nextDuration) =>
                  onChange({ ...value, duration: nextDuration })
                }
              />
            </FormRow>
          )}
        </div>
      </FormRow>

      <FormRow>
        <FormControlLabel
          control={
            <Switch
              checked={value.notify}
              onChange={() =>
                onChange({
                  ...value,
                  notify: !value.notify
                })
              }
              color="primary"
            />
          }
          label={
            <FlexContainer>
              <div>{t('Notify via Lessonly')}</div>
              <InfoCircle tooltip="If you would rather want to send information about this content via Dado, turn this option off." />
            </FlexContainer>
          }
        />
      </FormRow>
    </div>
  );
};

export const ActionSettingsLessonlyAssignToContentReadOnly = ({
  value,
  expTpl
}: {
  value: IAutomationActionTemplateLessonlyAssignToContent;
  expTpl: TaskBuilderContext;
}) => {
  const { t } = useUnsafeTranslation();
  return (
    <div>
      <FormRow>
        <DataFieldWithLabel label={t('Who should be assigned')}>
          {joinWithCommaAndAnd(
            value.pplToAdd.map((p) =>
              actionActorToString(p, expTpl.supportingActors, t)
            ),
            t
          )}
        </DataFieldWithLabel>
      </FormRow>

      <FormRow>
        <DataFieldWithLabel label={t('Assign to')}>
          {value.content.name}
        </DataFieldWithLabel>
      </FormRow>

      <FormRow>
        <DataFieldWithLabel label={t('Time to complete assignment')}>
          {value.duration === null
            ? t('No due date')
            : toWeekAndDayDuration(value.duration, t)}
        </DataFieldWithLabel>
      </FormRow>

      <FormRow>
        <DataFieldWithLabel label={t('Notify via Lessonly')}>
          {value.notify ? t('Yes') : t('No')}
        </DataFieldWithLabel>
      </FormRow>
    </div>
  );
};
